/**
 * Supabase Storage image transform helpers (web).
 * Match consumer `apps/consumer/lib/images.ts` — resize/compress for display, not originals.
 */

const OBJECT_PUBLIC = "/storage/v1/object/public/";
const RENDER_IMAGE_PUBLIC = "/storage/v1/render/image/public/";

export type ThumbTransformOpts = {
  w?: number;
  h?: number;
  q?: number;
  resize?: "cover" | "contain" | "fill";
};

/**
 * Menu detail / hero — scale only (contain). ~2x DPR; Supabase default cover crops.
 */
export const MENU_DETAIL_HERO_DISPLAY = {
  w: 720,
  q: 65,
  resize: "contain" as const,
};

export function isPrebakedMenuThumbUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== "string") return false;
  return /\/object\/public\/menu-thumbs\//i.test(url) || /\/menu-thumbs\/small\//i.test(url);
}

/**
 * Rewrite Supabase public object URLs to render/image with width/quality.
 * Non-Supabase / SVG / already-transformed URLs pass through unchanged.
 */
export function toThumbUrl(
  url: string | null | undefined,
  opts?: ThumbTransformOpts
): string | null {
  if (!url || typeof url !== "string" || url.trim() === "") return null;
  const u = url.trim();
  if (u.startsWith("data:") || u.startsWith("file:")) return u;
  if (u.includes("/storage/v1/render/image/public/")) return u;
  if (u.toLowerCase().endsWith(".svg")) return u;
  if (isPrebakedMenuThumbUrl(u)) return u;
  if (!u.includes(OBJECT_PUBLIC)) return u;

  try {
    const width = opts?.w ?? 800;
    const quality = Math.min(100, Math.max(20, opts?.q ?? 75));
    const transformed = u.replace(OBJECT_PUBLIC, RENDER_IMAGE_PUBLIC);
    const parsed = new URL(transformed);
    const params = new URLSearchParams(parsed.search);
    params.set("width", String(width));
    params.set("quality", String(quality));
    if (opts?.h != null) params.set("height", String(opts.h));
    if (opts?.resize) params.set("resize", opts.resize);
    parsed.search = params.toString();
    return parsed.toString();
  } catch {
    return u;
  }
}
